'use client';

import { memo } from 'react';
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  useFormState,
  UseFormTrigger,
} from 'react-hook-form';
import NextButton from '@/components/Button/NextButton';
import Input from '@/components/Input/Input';
import { FormData } from '@/types/form';
import { fieldRules } from '@/utils/form';
import generalStyles from '../FormBody.module.scss';
import StateSelect from './StateSelect/StateSelect';
import styles from './StepSix.module.scss';

interface Props {
  register?: UseFormRegister<FormData>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<FormData, any, FormData>;
  setValue?: UseFormSetValue<FormData>;
  trigger?: UseFormTrigger<FormData>;
  handleSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  closeModal?: () => void;
  formData?: FormData;
}

const AddressFormModalManual = ({
  register,
  control,
  setValue,
  trigger,
  handleSubmit,
  closeModal,
  formData,
}: Props) => {
  const { errors } = useFormState({ control });
  const handleChangeAddressVariant = () => {
    setValue?.('address_variant', 'google');
    trigger?.('address_variant');
  };

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isValid = await trigger?.();
    if (isValid) {
      await handleSubmit?.();
      closeModal?.();
    }
  };

  return (
    <form className={styles.form} onSubmit={submitForm}>
      <div className={styles.inputsWrapper}>
        <div className={styles.addressWrapper}>
          {register && (
            <Input
              label="Street address"
              placeholder="Your street"
              type="text"
              id="address"
              register={register('address', fieldRules.address)}
              error={errors?.address}
            />
          )}
          <button
            type="button"
            onClick={handleChangeAddressVariant}
            className={styles.variantButton}
          >
            Search for my address automatically
          </button>
        </div>
        <div className={styles.inputsRow}>
          {register && (
            <>
              <Input
                label="Apt/Suite"
                placeholder="Your apt/Suite"
                type="text"
                id="apt"
                register={register('apt')}
                error={errors?.apt}
              />
              <Input
                label="City"
                placeholder="Your city"
                type="text"
                id="city"
                register={register('city', fieldRules.city)}
                error={errors?.city}
              />
            </>
          )}
        </div>
        <div className={styles.inputsRow}>
          {register && (
            <>
              <StateSelect
                formData={formData}
                label="State"
                placeholder="Your state"
                id="state"
                register={register('state', fieldRules.state)}
                error={errors?.state}
                setValue={setValue}
                trigger={trigger}
              />
              <Input
                label="Zip code"
                placeholder="Your zip code"
                type="text"
                id="zip"
                register={register('zip', {
                  ...fieldRules.zip,
                  onChange: (e) => {
                    const { target } = e;
                    target.value = target.value.replace(/\D/g, '').slice(0, 5);
                  },
                })}
                error={errors?.zip}
              />
            </>
          )}
        </div>
      </div>
      <NextButton
        buttonType="submit"
        type="button"
        text="Apply changes"
        styles={`${generalStyles.button} ${generalStyles.smallButton}`}
      />
    </form>
  );
};

export default memo(AddressFormModalManual);
